import {
  Button,
  FormControl,
  FormControlLabel,
  Popover,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import FilterIcon from "@/app/assets/icons/filter.svg?react";
import { FC, useState } from "react";
import { useOpen } from "@/app/hooks";
import { Controller, UseFormReturn } from "react-hook-form";
import { DidoxModalFormValues } from "../types";
import { DateRangePicker } from "rsuite";
import "rsuite/dist/rsuite.min.css";
import CalendarIcon from "@/app/assets/icons/calendar.svg?react";

interface Props {
  form: UseFormReturn<DidoxModalFormValues>;
  onSubmit: (data: DidoxModalFormValues) => void;
  resetFilters: () => void;
}

export const DidoxDocumentsModalFilter: FC<Props> = ({
  form,
  onSubmit,
  resetFilters,
}) => {
  const { t } = useTranslation();
  const { open, setOpen } = useOpen();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const id = open ? "didox-documents-modal-filter" : undefined;

  const handleClick = () => {
    setOpen(!open);
  };

  const handleReset = () => {
    form.reset();
    resetFilters();
  };

  const handleOnSubmit = (data: DidoxModalFormValues) => {
    onSubmit(data);
    setOpen(false);
  };

  return (
    <>
      <Button
        startIcon={<FilterIcon />}
        variant="outlined"
        type="button"
        ref={setAnchorEl}
        aria-describedby={id}
        onClick={handleClick}
      >
        {t("filter")}
      </Button>
      <Popover
        id={id}
        open={open}
        onClose={handleClick}
        anchorEl={anchorEl}
        anchorOrigin={{
          horizontal: "left",
          vertical: "bottom",
        }}
        sx={{
          zIndex: 10001,
          "& .rs-picker-popup": {
            zIndex: "10002 !important",
          },
          "& .rs-picker-caret-icon": {
            fill: "none",
          },
        }}
        slotProps={{
          paper: {
            sx: {
              maxWidth: 480,
              width: "100%",
              p: 2,
              borderRadius: 4,
            },
          },
        }}
      >
        <form onSubmit={form.handleSubmit(handleOnSubmit)}>
          <Typography variant="h6" textAlign="center">
            {t("filter")}
          </Typography>

          <Controller
            control={form.control}
            name="dateType"
            render={({ field }) => (
              <FormControl>
                <RadioGroup
                  {...field}
                  row
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      fontSize: "1.4rem",
                    },
                  }}
                >
                  <FormControlLabel
                    value="created"
                    control={<Radio size="small" />}
                    label={t("on_created_date")}
                  />
                  <FormControlLabel
                    value="updated"
                    control={<Radio size="small" />}
                    label={t("on_updated_date")}
                  />
                </RadioGroup>
              </FormControl>
            )}
          />

          <Controller
            control={form.control}
            name="dates"
            render={({ field: { value, ...rest } }) => (
              <FormControl fullWidth>
                <DateRangePicker
                  value={value ? [value?.[0], value?.[1]] : null}
                  format="dd/MM/yyyy"
                  placement="bottomEnd"
                  caretAs={CalendarIcon}
                  {...rest}
                />
              </FormControl>
            )}
          />

          <Stack direction="row" spacing={2} justifyContent="center" mt={2}>
            <Button
              color="inherit"
              variant="outlined"
              type="button"
              onClick={handleReset}
            >
              {t("clear")}
            </Button>
            <Button color="primary" type="submit">
              {t("apply")}
            </Button>
          </Stack>
        </form>
      </Popover>
    </>
  );
};
